const DEFAULT_DECLINE_EXPLANATION =
    'The card was declined by the card issuer.'

const DECLINE_EXPLANATIONS: Readonly<Record<string, string>> = {
    insufficient_funds: 'The card has insufficient funds.',
    card_declined: DEFAULT_DECLINE_EXPLANATION,
    expired_card: 'The saved card has expired.',
    incorrect_cvc: 'The card security code was rejected.',
    processing_error:
        'The payment could not be processed because of a payment processor or card issuer error.',
    authentication_required:
        'The card issuer requires additional authentication, so the charge could not be completed in the background.',
    card_limit_exceeded:
        'The card’s transaction or spending limit was exceeded.',
    invalid_card_details: 'The saved card details are no longer valid.',
    card_restricted: 'The card was restricted from being used for this charge.',
}

export function paymentDeclineExplanation(code: unknown): string {
    if (typeof code !== 'string') {
        return DEFAULT_DECLINE_EXPLANATION
    }
    const normalizedCode = code.trim().toLowerCase().replaceAll('-', '_')
    return DECLINE_EXPLANATIONS[normalizedCode] ?? DEFAULT_DECLINE_EXPLANATION
}

export function chargeFailureMessage(error: unknown): string {
    const processing = paymentProcessingMessage(error)
    if (processing) {
        return processing
    }
    const response = asErrorResponse(error)
    if (response?.status !== 402) {
        return 'Couldn\'t complete the charge.'
    }
    const explanation = paymentDeclineExplanation(response.code)
    return `Couldn't complete the charge. ${explanation}`
}

export function isChargeFailure(error: unknown): boolean {
    const response = asErrorResponse(error)
    return response?.status === 402
        || response?.code === 'cancellation_charge_unconfirmed'
}

export function paymentProcessingMessage(error: unknown): string | null {
    const response = asErrorResponse(error)
    if (response?.status !== 409) {
        return null
    }
    if (response.code === 'payment_processing') {
        return 'Teya has not confirmed the outcome. Please do not submit another payment or refund. Contact support to check it.'
    }
    return null
}

function asErrorResponse(error: unknown): {
    status: number,
    code?: unknown,
} | null {
    if (typeof error !== 'object' || error === null || !('status' in error)) {
        return null
    }
    const status = (error as { status?: unknown }).status
    if (typeof status !== 'number') {
        return null
    }
    const response = error as {
        code?: unknown,
        error?: unknown,
        body?: unknown,
    }
    const code = codeFrom(response.error)
        ?? codeFrom(response.body)
        ?? response.code
    return { status, code }
}

function codeFrom(body: unknown): unknown {
    if (typeof body === 'object' && body !== null && 'code' in body) {
        return (body as { code?: unknown }).code
    }
    if (typeof body !== 'string') {
        return undefined
    }
    try {
        return codeFrom(JSON.parse(body) as unknown)
    } catch {
        return undefined
    }
}
